/**
 * Meta-Model Design Container Module
 * 
 * Dependency injection configuration for the meta-model-design bounded context.
 * Follows the createXModule pattern for consistency across all bounded contexts.
 */

import { ContainerModule } from 'inversify';
import { getMongoClient, DI_MONGO_CLIENT } from "@swoft/persistence";

// Domain Services
import { BoundedContextAnalysisService } from '../../application/services/BoundedContextAnalysisService';
import type { 
  IBoundedContextPatternRepository,
  IArchitecturalAnalysisRepository,
  IPackageScanner,
  IDependencyAnalyzer 
} from '../../application/services/BoundedContextAnalysisService';

// Infrastructure Implementations
import { MongoBoundedContextPatternRepository } from '../repositories/MongoBoundedContextPatternRepository';
import { MongoArchitecturalAnalysisRepository } from '../repositories/MongoArchitecturalAnalysisRepository';
import { FileSystemPackageScanner } from '../adapters/FileSystemPackageScanner';
import { SimpleDependencyAnalyzer } from '../adapters/SimpleDependencyAnalyzer';

// Infrastructure Dependencies are imported above

/**
 * Symbol constants for type-safe dependency injection
 */
export const META_MODEL_DESIGN_SYMBOLS = {
  // Application Services
  BoundedContextAnalysisService: 'BoundedContextAnalysisService',
  
  // Repository Ports
  IBoundedContextPatternRepository: 'IBoundedContextPatternRepository',
  IArchitecturalAnalysisRepository: 'IArchitecturalAnalysisRepository',
  
  // Infrastructure Ports
  IPackageScanner: 'IPackageScanner',
  IDependencyAnalyzer: 'IDependencyAnalyzer',
} as const;

/**
 * Database name for meta-model-design bounded context
 */
const databaseName = 'swoft-ddd-meta-model';

/**
 * Create Meta-Model Design Module
 * 
 * Configures dependency injection for the meta-model-design bounded context.
 * Follows Eric Evans DDD principles and Clean Architecture patterns.
 * 
 * @returns ContainerModule configured for meta-model-design bounded context
 */
export const createMetaModelDesignModule = (): ContainerModule => {
  return new ContainerModule((bind) => {
    // Repository bindings
    bind<IBoundedContextPatternRepository>(META_MODEL_DESIGN_SYMBOLS.IBoundedContextPatternRepository)
      .toDynamicValue((context) => {
        const mongoClient = context.container.get<import("mongodb").MongoClient>(DI_MONGO_CLIENT);
        return new MongoBoundedContextPatternRepository();
      })
      .inSingletonScope();

    bind<IArchitecturalAnalysisRepository>(META_MODEL_DESIGN_SYMBOLS.IArchitecturalAnalysisRepository)
      .toDynamicValue((context) => {
        const mongoClient = context.container.get<import("mongodb").MongoClient>(DI_MONGO_CLIENT);
        return new MongoArchitecturalAnalysisRepository();
      })
      .inSingletonScope();

    // Infrastructure service bindings
    bind<IPackageScanner>(META_MODEL_DESIGN_SYMBOLS.IPackageScanner)
      .to(FileSystemPackageScanner)
      .inSingletonScope();

    bind<IDependencyAnalyzer>(META_MODEL_DESIGN_SYMBOLS.IDependencyAnalyzer)
      .to(SimpleDependencyAnalyzer)
      .inSingletonScope();

    // Application service bindings
    bind<BoundedContextAnalysisService>(META_MODEL_DESIGN_SYMBOLS.BoundedContextAnalysisService)
      .toDynamicValue((context) => {
        return new BoundedContextAnalysisService(
          context.container.get<IBoundedContextPatternRepository>(META_MODEL_DESIGN_SYMBOLS.IBoundedContextPatternRepository),
          context.container.get<IArchitecturalAnalysisRepository>(META_MODEL_DESIGN_SYMBOLS.IArchitecturalAnalysisRepository),
          context.container.get<IPackageScanner>(META_MODEL_DESIGN_SYMBOLS.IPackageScanner),
          context.container.get<IDependencyAnalyzer>(META_MODEL_DESIGN_SYMBOLS.IDependencyAnalyzer)
        );
      })
      .inSingletonScope();
  });
};